import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import type { FC } from 'react';
import { useMemo } from 'react';

import { useUserProfile } from 'contexts/UserProfileContext';
import type { RepairOrderStatusE, WipStatusE } from 'declarations/enums/wip';
import type { OemFamilyClaimStatusesI } from 'declarations/interfaces/wip';
import { useFetchWipOemFamilyClaimStatuses } from 'hooks/api/useWip';
import {
  EMPTY_WIP_FILTERS,
  selectWipAppliedFilters,
  setWipAppliedFilters,
} from 'reduxStore/features/wip/wipSlice';
import { useAppDispatch, useAppSelector } from 'reduxStore/hooks';

type ChipItem = {
  key: string;
  label: string;
  onDelete: () => void;
};

const RO_STATUS_LABELS: Partial<Record<RepairOrderStatusE, string>> = {
  OpenProcessing: 'Open',
  Completed: 'Closed',
} as const;

const formatDate = (value: string | undefined): string =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    : '';

const WIPFilterChips: FC = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const applied = useAppSelector(selectWipAppliedFilters);
  const { userProfile } = useUserProfile();
  const { data: oemClaimStatuses } = useFetchWipOemFamilyClaimStatuses();

  const dealerNameById = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {};
    (userProfile?.mappedRoofTops ?? []).forEach((r): void => {
      map[r.id] = r.name;
    });
    return map;
  }, [userProfile?.mappedRoofTops]);

  const oemNameById = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {};
    (oemClaimStatuses ?? []).forEach((o: OemFamilyClaimStatusesI): void => {
      map[o.oemFamilyId] = o.oemFamilyName;
    });
    return map;
  }, [oemClaimStatuses]);

  const chips: ChipItem[] = useMemo(() => {
    const items: ChipItem[] = [];

    applied.roofTopIds.forEach((id): void => {
      items.push({
        key: `dealer-${id}`,
        label: dealerNameById[id] ?? `Dealer ${id}`,
        onDelete: (): void => {
          dispatch(
            setWipAppliedFilters({
              ...applied,
              roofTopIds: applied.roofTopIds.filter((v): boolean => v !== id),
            })
          );
        },
      });
    });

    applied.oemFamilyIds.forEach((id): void => {
      items.push({
        key: `oem-${id}`,
        label: oemNameById[id] ?? `OEM ${id}`,
        onDelete: (): void => {
          dispatch(
            setWipAppliedFilters({
              ...applied,
              oemFamilyIds: applied.oemFamilyIds.filter((v): boolean => v !== id),
            })
          );
        },
      });
    });

    applied.wipStatuses.forEach((s: WipStatusE): void => {
      items.push({
        key: `wip-${s}`,
        label: `WIP: ${s}`,
        onDelete: (): void => {
          dispatch(
            setWipAppliedFilters({
              ...applied,
              wipStatuses: applied.wipStatuses.filter((v): boolean => v !== s),
            })
          );
        },
      });
    });

    applied.claimStatuses.forEach((s): void => {
      items.push({
        key: `claim-${s}`,
        label: `Claim: ${s}`,
        onDelete: (): void => {
          dispatch(
            setWipAppliedFilters({
              ...applied,
              claimStatuses: applied.claimStatuses.filter((v): boolean => v !== s),
            })
          );
        },
      });
    });

    applied.repairOrderStatuses.forEach((s: RepairOrderStatusE): void => {
      items.push({
        key: `ro-${s}`,
        label: `RO: ${RO_STATUS_LABELS[s] ?? s}`,
        onDelete: (): void => {
          dispatch(
            setWipAppliedFilters({
              ...applied,
              repairOrderStatuses: applied.repairOrderStatuses.filter((v): boolean => v !== s),
            })
          );
        },
      });
    });

    if (applied.backlog !== null) {
      items.push({
        key: 'backlog',
        label: `Backlog: ${applied.backlog ? 'Yes' : 'No'}`,
        onDelete: (): void => {
          dispatch(setWipAppliedFilters({ ...applied, backlog: null }));
        },
      });
    }

    if (applied.createdAtFrom || applied.createdAtTo) {
      const from = formatDate(applied.createdAtFrom);
      const to = formatDate(applied.createdAtTo);
      let dateLabel = '';
      if (from && to) dateLabel = `${from} – ${to}`;
      else if (from) dateLabel = `From: ${from}`;
      else dateLabel = `To: ${to}`;
      items.push({
        key: 'date-range',
        label: dateLabel,
        onDelete: (): void => {
          dispatch(
            setWipAppliedFilters({
              ...applied,
              createdAtFrom: undefined,
              createdAtTo: undefined,
            })
          );
        },
      });
    }

    return items;
  }, [applied, dealerNameById, dispatch, oemNameById]);

  if (chips.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      {chips.map(
        (chip): JSX.Element => (
          <Chip
            key={chip.key}
            label={chip.label}
            size="small"
            color="primary"
            variant="filled"
            onDelete={chip.onDelete}
            sx={{ height: 24 }}
          />
        )
      )}
    </Box>
  );
};

// Re-export for ergonomic imports
export { EMPTY_WIP_FILTERS };
export default WIPFilterChips;
